import { MarketingEmailBlocker } from '../../../domain/marketing-email-blocker';
import { CreateMarketingEmailBlockerPageItemApiModel } from '@cxb/editor-stb-api';
import { mapperPipe, PlatformType, remainderMapper } from 'osn-shared';
import {
  createLocalizedText,
  platformTargetingMapper,
  scheduledDateMapperDomainToApi,
  targetingMapperDomainToApi,
  titleLocalizedFromHeadlineMapper,
} from '../../mapping/create-tray-domain-to-api';
import { MarketingEmailBlockerPageItemApiModel } from '@cxb/editor-stb-api/model/models';
import { commonMapper, headlineTextMapper, scheduledDateMapperApiToDomain } from '../../mapping/create-tray-api-to-domain';
import { MarketingEmailBlockerTrayViewModel } from './marketing-email-blocker-tray-form.view-model';
import { SchedulingType } from '../../../shared/enums/scheduling-type.enum';
import { TrayFormViewModelType } from '../view-model-common/view-model-type';

// 'MarketingEmailBlocker'

/**
 * DOMAIN -> API
 */

export const mapMarketingEmailBlockerTrayDomainModelToApiModel = (
  emailBlocker: MarketingEmailBlocker,
  pageVersionId: string,
  tenantId?: string
): CreateMarketingEmailBlockerPageItemApiModel => {
  const commonProperties = mapperPipe(
    emailBlocker,
    titleLocalizedFromHeadlineMapper,
    scheduledDateMapperDomainToApi,
    targetingMapperDomainToApi,
    platformTargetingMapper
  );
  return remainderMapper(commonProperties, () => ({
    tenantId,
    pageVersionId,
    hintTextLocalized: createLocalizedText('', ''),
    inputLabelLocalized: createLocalizedText(emailBlocker.inputFieldLabelEnglish, emailBlocker.inputFieldLabelArabic),
    descriptionLocalized: createLocalizedText(emailBlocker.descriptionTextEnglish, emailBlocker.descriptionTextArabic),
    buttonTextLocalized: createLocalizedText(emailBlocker.buttonTextEnglish, emailBlocker.buttonTextArabic),
    internalName: emailBlocker.titleEnglish,
  }));
};

/**
 * API -> DOMAIN
 */

export const mapMarketingEmailBlockerTrayApiModelToDomainModel = (
  emailBlocker: MarketingEmailBlockerPageItemApiModel
): MarketingEmailBlocker => {
  return remainderMapper(mapperPipe(emailBlocker, commonMapper, headlineTextMapper, scheduledDateMapperApiToDomain), () => ({
    type: 'MarketingEmailBlocker',
    titleEnglish: emailBlocker.internalName,
    descriptionTextEnglish: emailBlocker.descriptionLocalized?.en ?? '',
    descriptionTextArabic: emailBlocker.descriptionLocalized?.ar ?? '',
    buttonTextEnglish: emailBlocker.buttonTextLocalized.en,
    buttonTextArabic: emailBlocker.buttonTextLocalized.ar,
    inputFieldLabelEnglish: emailBlocker.inputLabelLocalized.en,
    inputFieldLabelArabic: emailBlocker.inputLabelLocalized.ar,
    userSegmentIds: emailBlocker.userSegments.map((userSegment) => userSegment.id),
    countryIds: emailBlocker.countries.map((country) => country.id),
  }));
};

/**
 * DOMAIN -> VIEWMODEL
 */

export const mapMarketingEmailBlockerTrayDomainModelToViewModel = (
  domainModel: MarketingEmailBlocker
): MarketingEmailBlockerTrayViewModel => {
  return {
    id: domainModel.id || '',
    moduleName: domainModel.titleEnglish,
    headlineText: { en: domainModel.headlineTextEnglish, ar: domainModel.headlineTextArabic },
    description: { en: domainModel.descriptionTextEnglish, ar: domainModel.descriptionTextArabic },
    inputFieldLabel: { en: domainModel.inputFieldLabelEnglish, ar: domainModel.inputFieldLabelArabic },
    buttonText: { en: domainModel.buttonTextEnglish, ar: domainModel.buttonTextArabic },
    scheduling: {
      schedulingType: domainModel.scheduledFrom || domainModel.scheduledTo ? SchedulingType.Schedule : SchedulingType.LiveWhenPublished,
      scheduledFrom: domainModel.scheduledFrom ? new Date(domainModel.scheduledFrom).toISOString() : undefined,
      scheduledTo: domainModel.scheduledTo ? new Date(domainModel.scheduledTo).toISOString() : undefined,
    },
    userSegmentIds: domainModel.userSegmentIds,
    countryIds: domainModel.countryIds,
    type: TrayFormViewModelType.MarketingEmailBlocker,
  };
};

/**
 * VIEWMODEL -> DOMAIN
 */

export const mapMarketingEmailBlockerTrayViewModelToDomainModel = (
  emailBlocker: MarketingEmailBlockerTrayViewModel,
  platformIds: Record<PlatformType, string>
): MarketingEmailBlocker => {
  return {
    id: emailBlocker.id || '',
    titleEnglish: emailBlocker.moduleName,
    headlineTextEnglish: emailBlocker.headlineText.en,
    headlineTextArabic: emailBlocker.headlineText.ar,
    descriptionTextEnglish: emailBlocker.description.en,
    descriptionTextArabic: emailBlocker.description.ar,
    inputFieldLabelEnglish: emailBlocker.inputFieldLabel.en,
    inputFieldLabelArabic: emailBlocker.inputFieldLabel.ar,
    buttonTextEnglish: emailBlocker.buttonText.en,
    buttonTextArabic: emailBlocker.buttonText.ar,
    scheduledFrom: emailBlocker.scheduling?.scheduledFrom ? new Date(emailBlocker.scheduling.scheduledFrom).getTime() : undefined,
    scheduledTo: emailBlocker.scheduling?.scheduledTo ? new Date(emailBlocker.scheduling.scheduledTo).getTime() : undefined,
    userSegmentIds: emailBlocker.userSegmentIds,
    countryIds: emailBlocker.countryIds,
    type: 'MarketingEmailBlocker',
    order: undefined,
    visible: undefined,
    draggable: true,
    platformIds: Object.values(platformIds),
  };
};
